/** Career chapters along the lane — environment tells the story. */

export type ChapterId = 'learn' | 'build' | 'scale' | 'ai' | 'climax';

export interface ChapterDef {
  id: ChapterId;
  title: string;
  subtitle: string;
  /** Inclusive start Z (player moves toward more negative) */
  zStart: number;
  zEnd: number;
  fog: string;
  ambient: string;
  accent: string;
  sky: string;
  holograms: string[];
}

export const CHAPTERS: ChapterDef[] = [
  {
    id: 'learn',
    title: 'Chapter 1 — Learn',
    subtitle: 'HTML · CSS · JavaScript',
    zStart: 5,
    zEnd: -35,
    fog: '#0a1628',
    ambient: '#3a6ea5',
    accent: '#00c8ff',
    sky: '#071018',
    holograms: [
      'GitHub Commits',
      'HTML / CSS',
      'JavaScript',
      'First Line of Code',
    ],
  },
  {
    id: 'build',
    title: 'Chapter 2 — Build',
    subtitle: 'React · Node · MongoDB',
    zStart: -35,
    zEnd: -70,
    fog: '#0a1a12',
    ambient: '#2d6a4f',
    accent: '#39ff14',
    sky: '#06140c',
    holograms: [
      'React Components',
      'Node APIs',
      'MongoDB',
      'Architecture Diagrams',
    ],
  },
  {
    id: 'scale',
    title: 'Chapter 3 — Scale',
    subtitle: 'Docker · Kubernetes · Azure',
    zStart: -70,
    zEnd: -105,
    fog: '#1a1208',
    ambient: '#c45c26',
    accent: '#ff9f1c',
    sky: '#140e06',
    holograms: [
      'Docker Containers',
      'CI/CD Pipeline',
      'Azure Dashboard',
      'Deployment Logs',
    ],
  },
  {
    id: 'ai',
    title: 'Chapter 4 — AI',
    subtitle: 'LLMs · RAG · Agents',
    zStart: -105,
    zEnd: -138,
    fog: '#140a1c',
    ambient: '#7c3aed',
    accent: '#c084fc',
    sky: '#0c0614',
    holograms: [
      'LLM Responses',
      'Vector Embeddings',
      'Neural Nets',
      'LangChain Agents',
    ],
  },
  {
    id: 'climax',
    title: 'Chapter 5 — The Final',
    subtitle: 'Portfolio Reveal',
    zStart: -138,
    zEnd: -200,
    fog: '#1a1408',
    ambient: '#f4c500',
    accent: '#d9ff00',
    sky: '#120e06',
    holograms: ['CredNova', 'LearnForge', 'MADAlgos', 'Leadgate'],
  },
];

export function chapterAtZ(z: number): ChapterDef {
  for (const c of CHAPTERS) {
    if (z <= c.zStart && z > c.zEnd) return c;
  }
  if (z > CHAPTERS[0].zStart) return CHAPTERS[0];
  return CHAPTERS[CHAPTERS.length - 1];
}

/** Project arena portal beats along the lane. */
export const PROJECT_ARENAS = [
  {
    id: 'crednova',
    label: 'CredNova Arena',
    blurb: 'Fintech data centre — SHAP risk scores, loan gauges, credit graphs.',
    z: -48,
    color: '#00c8ff',
  },
  {
    id: 'learnforge',
    label: 'LearnForge Arena',
    blurb: 'Futuristic AI library — RAG, Bayesian knowledge graphs & MCP tool orbit.',
    z: -88,
    color: '#c084fc',
  },
  {
    id: 'madalgos',
    label: 'MADAlgos Arena',
    blurb: 'Coding stadium — Judge0 terminals, cloud infra, holographic seats.',
    z: -118,
    color: '#ff9f1c',
  },
] as const;
